import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { FileText, Search, Download, Upload, Trash2, FolderOpen, FileDigit, FileSpreadsheet, Image as ImageIcon, FileType, X } from 'lucide-react';
import { theme, cardStyle, inputStyle, btnStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  SOP LIBRARY PAGE
//  Browse all NABH SOPs, MOUs, registers, licenses etc. from nabh/
//  folder. Clinic name auto-replaces "SDH/Siddharth" in display.
//  Admin can upload new SOPs and delete existing ones.
// ═══════════════════════════════════════════════════════════════════

const FILE_TYPE_ICON = {
  pdf:        <FileDigit size={20} color="#dc2626" />,
  word:       <FileText size={20} color="#2563eb" />,
  excel:      <FileSpreadsheet size={20} color="#16a34a" />,
  powerpoint: <FileType size={20} color="#ea580c" />,
  image:      <ImageIcon size={20} color="#7c3aed" />,
  other:      <FileText size={20} color="#6b7280" />,
};

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const SOPLibraryPage = ({ API_BASE, currentUser }) => {
  const [data, setData] = useState({ categories: [], files: [], total: 0, clinic: { name: '', short: '' } });
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState('all');
  const [search, setSearch] = useState('');
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const isAdmin = ['owner','admin','super_admin'].includes(currentUser?.role);
  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: '', text: '' }), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/sop-library`);
      setData(res.data);
    } catch (e) {
      showMsg('error', 'SOP library load nahi ho saki: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let list = data.files;
    if (activeCat !== 'all') list = list.filter(f => f.category === activeCat);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(f =>
        f.displayName.toLowerCase().includes(q) ||
        f.name.toLowerCase().includes(q) ||
        f.categoryLabel.toLowerCase().includes(q)
      );
    }
    return list;
  }, [data.files, activeCat, search]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) return showMsg('error', 'Max 25 MB allowed');
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API_BASE}/sop-library/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      showMsg('success', `${file.name} upload ho gaya`);
      load();
    } catch (err) {
      showMsg('error', 'Upload failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`"${filename}" delete kar dein?`)) return;
    try {
      await axios.delete(`${API_BASE}/sop-library/file/${encodeURIComponent(filename)}`);
      showMsg('success', 'Delete ho gaya');
      load();
    } catch (err) {
      showMsg('error', 'Delete failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleOpen = (f) => {
    const tok = sessionStorage.getItem('token') || localStorage.getItem('token') || '';
    const url = `${API_BASE.replace(/\/api$/, '')}/sop-files/${encodeURIComponent(f.name)}?t=${encodeURIComponent(tok)}`;
    if (f.type === 'pdf' || f.type === 'image') {
      window.open(url, '_blank');
    } else {
      // Word/Excel/PPT — direct download
      const a = document.createElement('a');
      a.href = url;
      a.download = f.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  };

  if (loading) return (
    <div style={{ ...cardStyle, textAlign: 'center', padding: 60 }}>
      <FolderOpen size={48} color={theme.primary} style={{ marginBottom: 12, opacity: 0.5 }} />
      <p style={{ color: theme.textMuted }}>SOP Library load ho rahi hai…</p>
    </div>
  );

  return (
    <div>
      {/* ─── HEADER ─── */}
      <div style={{ ...cardStyle, marginBottom: 16, background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`, color: 'white', border: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, color: 'white', fontSize: '1.4rem' }}>📚 SOP & NABH Document Library</h2>
            <p style={{ margin: '4px 0 0', opacity: 0.9, fontSize: 13 }}>
              {data.clinic?.name || 'Your Clinic'} · {data.total} documents · auto-customized with your clinic name
            </p>
          </div>
          {isAdmin && (
            <label style={{ ...btnStyle, marginTop: 0, cursor: 'pointer', backgroundColor: 'rgba(255,255,255,0.18)', color: 'white', border: '1px solid rgba(255,255,255,0.4)' }}>
              <Upload size={16} />
              <span style={{ marginLeft: 6 }}>{uploading ? 'Uploading…' : 'Upload SOP'}</span>
              <input type="file" hidden onChange={handleUpload} accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png" disabled={uploading} />
            </label>
          )}
        </div>
      </div>

      {/* ─── MESSAGE ─── */}
      {msg.text && (
        <div style={{
          padding: '10px 14px', marginBottom: 12, borderRadius: 8,
          background: msg.type === 'error' ? '#fee' : '#efe',
          color: msg.type === 'error' ? theme.danger : theme.success,
          border: `1px solid ${msg.type === 'error' ? theme.danger : theme.success}44`,
          fontWeight: 600, fontSize: 13.5,
        }}>{msg.text}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 16, alignItems: 'start' }}>
        {/* ─── CATEGORY SIDEBAR ─── */}
        <div style={{ ...cardStyle, position: 'sticky', top: 16, padding: 12 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, padding: '8px 8px 4px', margin: 0 }}>
            Categories
          </p>
          <button onClick={() => setActiveCat('all')} style={catBtnStyle(activeCat === 'all')}>
            <span style={{ marginRight: 8 }}>📁</span> All Documents
            <span style={countBadgeStyle(activeCat === 'all')}>{data.total}</span>
          </button>
          {data.categories.map(c => (
            <button key={c.key} onClick={() => setActiveCat(c.key)} style={catBtnStyle(activeCat === c.key)}>
              <span style={{ marginRight: 8 }}>{c.icon}</span>
              <span style={{ flex: 1, textAlign: 'left' }}>{c.label}</span>
              <span style={countBadgeStyle(activeCat === c.key)}>{c.count}</span>
            </button>
          ))}
        </div>

        {/* ─── FILES GRID ─── */}
        <div>
          {/* Search bar */}
          <div style={{ ...cardStyle, padding: '8px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
            <Search size={18} color={theme.textMuted} />
            <input
              type="text"
              placeholder="Search by name or category…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ ...inputStyle, border: 'none', boxShadow: 'none', padding: '8px 0', flex: 1 }}
            />
            {search && (
              <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.textMuted }}>
                <X size={18} />
              </button>
            )}
            <span style={{ fontSize: 12.5, color: theme.textMuted, fontWeight: 600 }}>
              {filtered.length} file{filtered.length !== 1 ? 's' : ''}
            </span>
          </div>

          {filtered.length === 0 ? (
            <div style={{ ...cardStyle, textAlign: 'center', padding: 60 }}>
              <FolderOpen size={48} color={theme.textSubtle} style={{ marginBottom: 12, opacity: 0.5 }} />
              <p style={{ color: theme.textMuted, fontWeight: 600 }}>Koi document nahi mila</p>
              <p style={{ fontSize: 13, color: theme.textSubtle }}>Search clear karein ya doosri category dekhein</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
              {filtered.map(f => (
                <div key={f.name} style={fileCardStyle}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                    <div style={{ background: '#f4f7fa', padding: 8, borderRadius: 8 }}>
                      {FILE_TYPE_ICON[f.type] || FILE_TYPE_ICON.other}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 13.5, color: theme.navy, lineHeight: 1.3, wordBreak: 'break-word' }}>
                        {f.displayName}
                      </div>
                      <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 3 }}>
                        <span style={{ background: theme.borderLight, padding: '1px 6px', borderRadius: 4, marginRight: 6 }}>{f.ext.toUpperCase()}</span>
                        {formatSize(f.size)}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => handleOpen(f)} style={{ ...actionBtnStyle, flex: 1 }}>
                      <Download size={13} style={{ marginRight: 4 }} />
                      {f.type === 'pdf' || f.type === 'image' ? 'View' : 'Download'}
                    </button>
                    {isAdmin && (
                      <button onClick={() => handleDelete(f.name)} style={{ ...actionBtnStyle, padding: '6px 10px', backgroundColor: theme.danger + '15', color: theme.danger, border: `1px solid ${theme.danger}33` }}>
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Inline styles ───
const catBtnStyle = (active) => ({
  display: 'flex', alignItems: 'center', width: '100%',
  padding: '9px 10px', marginBottom: 2, borderRadius: 8,
  border: 'none', background: active ? theme.primary + '18' : 'transparent',
  color: active ? theme.primaryDark : theme.navy,
  fontWeight: active ? 700 : 500,
  cursor: 'pointer', fontSize: 13, transition: '0.15s',
  textAlign: 'left',
});
const countBadgeStyle = (active) => ({
  marginLeft: 'auto', fontSize: 11, fontWeight: 700,
  background: active ? theme.primary : theme.borderLight,
  color: active ? 'white' : theme.textMuted,
  padding: '2px 7px', borderRadius: 999,
});
const fileCardStyle = {
  background: 'white', padding: 12, borderRadius: 10,
  border: `1px solid ${theme.border}`,
  boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
  transition: 'all 0.15s',
};
const actionBtnStyle = {
  padding: '6px 12px', borderRadius: 6,
  border: `1px solid ${theme.primary}33`,
  background: theme.primary + '12',
  color: theme.primary,
  cursor: 'pointer', fontSize: 12, fontWeight: 600,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};

export default SOPLibraryPage;
